/**
 * Auth Test Helper
 * 
 * Utility functions to test authentication and seller registration flows
 */

import axios from 'axios';

// Reset authentication state (logout)
export const resetAuth = async () => {
  console.log('🧪 Test: Resetting authentication state...');
  
  // Check if token exists before trying to remove it
  const token = localStorage.getItem('token');
  if (token) {
    console.log('Token found, removing it');
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    console.log('✅ Authentication state reset');
  } else {
    console.log('No token found, user is already logged out');
  }
  
  // Double check that auth header is cleared
  if (axios.defaults.headers.common['Authorization']) {
    delete axios.defaults.headers.common['Authorization'];
    console.log('Cleared Authorization header');
  }
  
  return true;
};

// Test login
export const testLogin = async (email, password) => {
  console.log(`🧪 Test: Attempting login with ${email}...`);
  try {
    if (!email || !password) {
      console.error('❌ Login failed: Email and password are required');
      return false;
    }
    
    console.log('Making login API request...');
    const response = await axios.post('/api/auth/login', { email, password });
    console.log('Received login response');
    
    // Check for token and user object in response
    if (!response.data) {
      console.error('❌ Login failed: No data in response');
      return false;
    }
    
    const { token, user } = response.data;
    
    if (!token) {
      console.error('❌ Login failed: No token received from server');
      return false;
    }
    
    if (!user) {
      console.warn('⚠️ Login partially successful: Token received but no user object');
    } else {
      console.log('User data received:', {
        id: user.id,
        username: user.username,
        role: user.role
      });
    }
    
    // Store token in localStorage
    console.log('Saving token to localStorage');
    localStorage.setItem('token', token);
    
    // Set axios default header
    console.log('Setting Authorization header for future requests');
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    console.log('✅ Login successful');
    return true;
  } catch (error) {
    console.error('❌ Login failed:', error.message);
    if (error.response) {
      console.error('Status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received from server. Check that the backend is running.');
    }
    return false;
  }
};

// Test become seller
export const testBecomeSeller = async () => {
  console.log('🧪 Test: Starting seller registration flow...');
  try {
    // First get the current user ID from the API
    console.log('Fetching current user ID from /api/auth/user...');
    const userResponse = await axios.get('/api/auth/user');
    const userId = userResponse.data.id;
    
    if (!userId) {
      console.error('❌ Could not get user ID from API');
      return false;
    }
    
    console.log(`Got user ID: ${userId}`);
    
    // Now try the direct update role endpoint first (most reliable)
    console.log(`Calling direct update role endpoint: /api/auth/direct-update-role/${userId}`);
    try {
      const directResponse = await axios.post(`/api/auth/direct-update-role/${userId}`, {
        role: 'seller'
      });
      
      console.log('Direct role update response:', directResponse.data);
      
      if (directResponse.data?.token) {
        console.log('✅ Received new token with seller role');
        localStorage.setItem('token', directResponse.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${directResponse.data.token}`;
        console.log('✅ Seller role update successful via direct endpoint');
        return true;
      }
    } catch (directError) {
      console.error('Direct role update failed:', directError.message);
      // Continue to next method
    }
    
    // Try the force-seller endpoint (requires auth, uses token)
    console.log('Calling force-seller endpoint: /api/auth/force-seller');
    try {
      const forceResponse = await axios.post('/api/auth/force-seller');
      
      console.log('Force seller response:', forceResponse.data);
      
      if (forceResponse.data?.token) {
        console.log('✅ Received new token with seller role');
        localStorage.setItem('token', forceResponse.data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${forceResponse.data.token}`;
        console.log('✅ Seller role update successful via force endpoint');
        return true;
      }
    } catch (forceError) {
      console.error('Force seller endpoint failed:', forceError.message);
      // Continue to next method
    }
    
    // Finally try the webhook endpoint as last resort
    console.log(`Calling webhook endpoint: /api/webhooks/test-set-seller-role with userId=${userId}`);
    const response = await axios.post('/api/webhooks/test-set-seller-role', {
      userId: userId
    });
    
    console.log('Webhook endpoint response:', response.data);
    
    if (response.data?.token) {
      console.log('✅ Received new token with seller role');
      localStorage.setItem('token', response.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
      console.log('✅ Seller role update successful via webhook endpoint');
      return true;
    } else if (response.data?.success) {
      console.log('⚠️ Webhook request was successful but no token received');
      console.log('✅ Seller registration appears successful');
      return true;
    }
    
    console.error('❌ All seller role update methods failed');
    return false;
  } catch (error) {
    console.error('❌ Seller registration failed with error:', error.message);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    } else if (error.request) {
      console.error('No response received from server');
    }
    return false;
  }
};

// Test check auth
export const testCheckAuth = async () => {
  console.log('🧪 Test: Checking current auth state...');
  try {
    // First check if token exists
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('❌ No token found in localStorage');
      return null;
    }
    
    // Make sure token is set in headers
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    // Call the user endpoint to get current user data
    const response = await axios.get('/api/auth/user');
    console.log('✅ Auth state:', response.data);
    
    // Decode token to verify role
    try {
      const tokenParts = token.split('.');
      const payload = JSON.parse(atob(tokenParts[1]));
      console.log('📝 Token contains role:', payload.role);
      console.log('📝 Token expires:', new Date(payload.exp * 1000).toLocaleString());
      
      // Check if role in token matches role from server
      if (payload.role !== response.data.role) {
        console.warn('⚠️ Warning: Role in token does not match role from server');
        console.warn(`Token: ${payload.role}, Server: ${response.data.role}`);
      }
    } catch (e) {
      console.error('❌ Error decoding token:', e);
    }
    
    return response.data;
  } catch (error) {
    console.error('❌ Error checking auth:', error.message);
    if (error.response) {
      console.error('Response status:', error.response.status);
      console.error('Response data:', error.response.data);
    }
    return null;
  }
};

// Test the full seller flow: login, become seller, and check auth state
export const testFullSellerFlow = async (email, password) => {
  console.log('🧪 STARTING FULL SELLER FLOW TEST 🧪');
  
  try {
    // Reset auth state
    await resetAuth();
    
    // Login
    const loginResult = await testLogin(email, password);
    if (!loginResult) {
      console.error('❌ Seller flow test failed at login step');
      return false;
    }
    
    // Wait a moment to ensure login is processed
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Check auth state
    const initialAuthState = await testCheckAuth();
    console.log('Initial state before becoming seller:', initialAuthState?.role || 'unknown');
    
    // Become seller
    const sellerResult = await testBecomeSeller();
    if (!sellerResult) {
      console.error('❌ Seller flow test failed at become seller step');
      return false;
    }
    
    // Wait for auth state to update - this is crucial for the navbar to update
    console.log('Waiting for auth state to update (1 second)...');
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Force refresh token in local storage
    console.log('Refreshing auth state after role change...');
    axios.defaults.headers.common['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
    
    // Check auth state after becoming seller
    const finalAuthState = await testCheckAuth();
    
    // Compare initial and final state
    console.log(`Role change: ${initialAuthState?.role || 'unknown'} → ${finalAuthState?.role || 'unknown'}`);
    
    if (finalAuthState?.role === 'seller') {
      console.log('✅ SELLER FLOW TEST SUCCESSFUL');
      console.log('✅ User is now a seller with role:', finalAuthState.role);
      return true;
    } else {
      console.error('❌ Seller flow test completed but user role is not seller');
      console.error('Current role:', finalAuthState?.role);
      return false;
    }
  } catch (error) {
    console.error('❌ Error in full seller flow test:', error.message);
    return false;
  }
};

// Test the navbar state to ensure it's showing correct auth status
export const testNavbarState = async () => {
  console.log('🧪 Test: Checking navbar state...');
  
  try {
    // Check for token
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('❓ No token found - navbar should show "Login" button');
      return null;
    }
    
    // Get the user data from context
    console.log('Fetching current user data...');
    try {
      // Make sure authorization header is set
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      const response = await axios.get('/api/auth/user');
      const userData = response.data;
      
      // Log what the navbar should be showing
      console.log('✅ Current user state that navbar should reflect:', {
        isLoggedIn: true,
        role: userData?.role,
        username: userData?.username
      });
      
      // Force the navbar to refresh by checking the current URL
      console.log('💡 To ensure navbar updates correctly:');
      console.log('1. Check if "Login" button is visible (should be hidden if logged in)');
      console.log('2. Check if user dropdown is visible (should be visible if logged in)');
      console.log('3. Check if "Seller Dashboard" is visible (should be visible if role is seller)');
      
      // If there are issues with the navbar, suggest fixes
      console.log('\n💡 If the navbar is not showing correctly:');
      console.log('1. Try reloading the page (F5)');
      console.log('2. Try clicking the "Force Refresh UI" button');
      console.log('3. Check browser console for any errors');
      console.log('4. Ensure the token in localStorage is valid (check expiration)');
      
      return userData;
    } catch (error) {
      console.error('❌ Error fetching user data:', error.message);
      console.log('💡 Navbar issue diagnosis:');
      console.log('- Token may be invalid or expired');
      console.log('- Backend server may be offline');
      console.log('- Auth endpoints may be returning errors');
      return null;
    }
  } catch (error) {
    console.error('❌ Error checking navbar state:', error.message);
    return null;
  }
};

// Create a named object for export
const authTestUtils = {
  resetAuth,
  testLogin,
  testBecomeSeller,
  testCheckAuth,
  testFullSellerFlow,
  testNavbarState
};

export default authTestUtils; 